import { AppDispatch, RootState } from "app/store";
import {
  selectTrialList,
  selectInitializeState,
  TrialProduct,
  InitializeState,
  initialAsync,
  trialAsync,
} from "features/trial";
import React, { useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled, { css } from "styled-components";
import OmronHeader from "components/OmronHeader";
import OmronFooter from "components/OmronFooter";
import OmronSidebar from "components/OmronSidebar";
import { logoutAsync, userAsync } from "features/auth/operations";
import { selectUserState } from "features/auth/selectors";
import PageTitle from "components/PageTitle";
import { useTranslation } from "react-i18next";
import {
  Column,
  Item,
  Loading,
  Message,
  Table,
  TableHeader,
  ToolTip,
} from "components/Table";

const TrialPage: React.FC = (): JSX.Element => {
  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();
  const products = useSelector<RootState, TrialProduct[]>((state) =>
    selectTrialList(state)
  );
  const initializeState = useSelector<RootState, InitializeState>((state) =>
    selectInitializeState(state)
  );
  const user = useSelector(selectUserState);

  useEffect(() => {
    dispatch(userAsync());
    dispatch(initialAsync());
  }, [dispatch]);

  const logout = useCallback(async () => {
    const { meta } = await dispatch(logoutAsync());
    if (meta.requestStatus === "rejected") {
      return false;
    }
    window.location.pathname = "/";
    return true;
  }, [dispatch]);

  const splitTextByPeriod = (text: string): string[] => {
    const regex = /([。.])/g;

    return text.split(regex).reduce((acc: string[], part) => {
      if (regex.test(part)) {
        if (acc.length > 0) {
          acc[acc.length - 1] += part;
        } else {
          acc.push(part);
        }
      } else if (part.trim()) {
        acc.push(part.trim());
      }
      return acc;
    }, []);
  };
  return (
    <div>
      <Wrap>
        <OmronHeader
          link={{ label: t("common.label.breadcrumbsHome"), path: "/" }}
          label={[t("common.label.breadcrumbsTrial")]}
          user={user}
        />
        <OmronSidebar trial onLogout={logout} />
        <Main>
          <PageTitle title={t("trialPage.label.title")} text="" />
          <Table>
            <thead>
              <TableHeader>
                <ProductNameColumn>
                  {t("trialPage.table.productName")}
                  <ToolTip className="tooltip">
                    {t("trialPage.help.productName")}
                  </ToolTip>
                </ProductNameColumn>
                <LicenseTypeColumn>
                  {t("trialPage.table.licenseModel")}
                  <ToolTip className="tooltip">
                    {t("trialPage.help.licenseModel")}
                  </ToolTip>
                </LicenseTypeColumn>
                <LicenseDescColumn>
                  {t("trialPage.table.licenseDescription")}
                  <ToolTip className="tooltip">
                    {t("trialPage.help.licenseDescription")}
                  </ToolTip>
                </LicenseDescColumn>
                <ButtonColumn>
                  {t("trialPage.table.trialRequest")}
                  <ToolTip className="tooltip">
                    {t("trialPage.help.trialRequest")}
                  </ToolTip>
                </ButtonColumn>
              </TableHeader>
            </thead>
            <tbody>
              {initializeState === "Initializing" && (
                <tr>
                  <td colSpan={4}>
                    <Loading />
                  </td>
                </tr>
              )}
              {initializeState === "initializeError" && (
                <tr>
                  <td colSpan={11}>
                    <Message style={{ color: "red" }}>
                      {t("trialPage.message.faildGetData")}
                    </Message>
                  </td>
                </tr>
              )}
              {initializeState === "Initialized" && products.length === 0 && (
                <tr>
                  <td colSpan={11}>
                    <Message>{t("trialPage.message.dataEmpty")}</Message>
                  </td>
                </tr>
              )}
              {initializeState === "Initialized" &&
                products.length !== 0 &&
                products.map((data) => (
                  <tr key={`${data.name}`}>
                    <Item key={`${data.name}_name`}>{data.name}</Item>
                    <Item key={`${data.name}_license`}>{data.licensename}</Item>
                    <Item key={`${data.name}_trialRequestUrl`}>
                      {splitTextByPeriod(
                        data.name === "SysmacStudio"
                          ? t("trialPage.label.freeLicenseDescription")
                          : ""
                      ).map((line, idx) => (
                        <span
                          key={idx}
                          style={{ display: "block", textAlign: "left" }}
                        >
                          {line}
                        </span>
                      ))}
                    </Item>
                    <Item key={`${data.name}_trialRequest`}>
                      <Button
                        onClick={() => {
                          dispatch(
                            trialAsync({
                              userId: user,
                              productId: data.id,
                              featureId: data.fid,
                              licenseId: data.lid,
                            })
                          );
                        }}
                      >
                        {t("trialPage.button.trialRequest")}
                      </Button>
                    </Item>
                  </tr>
                ))}
            </tbody>
          </Table>
        </Main>
        <OmronFooter />
      </Wrap>
    </div>
  );
};

export default TrialPage;

const Wrap = styled.div`
  display: grid;
  grid-template-rows: 76px 40px 1fr auto;
  grid-template-columns: 20rem 1fr;
  grid-template-areas: "header header" "breadc breadc" "side   main" "side   footer";
  min-height: 100vh;
`;

const Main = styled.main`
  grid-area: main;
  padding: 3.2rem 50px;
`;

const ButtonColumn = styled(Column)`
  width: 14%;
`;
const ProductNameColumn = styled(Column)`
  width: 152px;
  padding: 9.8px;
`;

const LicenseTypeColumn = styled(Column)`
  width: 172px;
  padding: 9.8px;
`;

const LicenseDescColumn = styled(Column)`
  width: 400px;
  padding: 9.8px;
  white-space: normal;
  text-align: center;
  vertical-align: middle;
  line-height: 1.4;
  letter-spacing: 0;
  font-weight: normal;
  word-wrap: break-word;
  span {
    display: block;
    margin-bottom: 4px;
  }
`;

const Button = styled.a<{
  disabled?: boolean;
}>`
  display: block;
  padding: 12px 0;
  color: #ffffff;
  text-decoration: none;
  background: #005eb8;
  border: 1px #005eb8 solid;
  -moz-transition: all 0.3s ease-out;
  -ms-transition: all 0.3s ease-out;
  -webkit-transition: all 0.3s ease-out;
  transition: all 0.3s ease-out;
  cursor: pointer;
  :hover {
    background: rgba(0, 94, 184, 0.7);
  }
  ${({ disabled }) =>
    disabled &&
    css`
      color: #999999;
      background: #f5f5f5;
      border: 1px #e6e6e6 solid;
      pointer-events: none;
      :hover {
        background: #fafafa;
      }
    `}
`;
